/**
 * `/__ui/forms` — the visual-regression harness for M4.V8, the same trick B4's board and M5's
 * bell use. It renders with **no session, no api and no database**, so a served build can be
 * hit-tested against `discovery/ui-reference/forms/sandbox-public-*.png`.
 *
 * Surfaces, selected by `?screen=`:
 *   `public` (default) · `validation` · `thankyou` · `builder`
 *
 * The fixture is the sandbox form the round-5h capture created, question for question, so the
 * numbers the verifier compares are the numbers monday rendered.
 */
import { useMemo } from 'react';
import {
  formAppearanceSchema,
  formQuestionSchema,
  formSettingsSchema,
  type FormQuestion,
} from '@elizmonday/shared';
import { FormBuilder, type BuilderForm } from './FormBuilder';
import { PublicFormPage } from './PublicFormPage';
import type { PublicForm } from './types';

/** The sandbox board's own columns, in its own order (`sandbox-form-view-row.json`). */
const QUESTIONS: FormQuestion[] = [
  { id: 'name', kind: 'name', title: 'Name', required: true, structural: true },
  { id: 'status', kind: 'single_select', title: 'Status', options: [
    { value: '0', label: 'Working on it', visible: true },
    { value: '1', label: 'Done', visible: true },
    { value: '2', label: 'Stuck', visible: true },
  ] },
  { id: 'dropdown', kind: 'multi_select', title: 'Dropdown', options: [
    { value: '1', label: 'Design', visible: true },
    { value: '2', label: 'Engineering', visible: true },
  ] },
  { id: 'text', kind: 'short_text', title: 'Text (renamed)' },
  { id: 'long_text', kind: 'long_text', title: 'Long Text' },
  { id: 'numbers', kind: 'number', title: 'Numbers' },
  { id: 'date', kind: 'date', title: 'Date' },
  { id: 'checkbox', kind: 'boolean', title: 'Checkbox' },
].map((question) => formQuestionSchema.parse(question));

function fixture(overrides: Partial<PublicForm> = {}): PublicForm {
  return {
    token: 'f'.repeat(32),
    title: 'Sandbox – Column types',
    description: null,
    boardId: '18430299563',
    renderId: 'harness',
    questions: QUESTIONS,
    settings: formSettingsSchema.parse({}),
    appearance: formAppearanceSchema.parse({}),
    ...overrides,
  };
}

export function FormsHarness() {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const screen = params.get('screen') ?? 'public';
  const form = useMemo(() => fixture(), []);

  if (screen === 'builder') {
    const builderForm: BuilderForm = { ...form, id: '1000000000001', active: true, url: `/forms/${form.token}` };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }} data-testid="ui-forms-builder">
        <FormBuilder form={builderForm} onBack={() => undefined} onPreview={() => undefined} onShare={() => undefined} />
      </div>
    );
  }

  if (screen === 'thankyou') {
    // The thank-you page is reached by submitting; the harness stubs the endpoint so the
    // served build can be photographed without an api.
    if (typeof window !== 'undefined' && !('__formsHarnessPatched' in window)) {
      (window as unknown as Record<string, unknown>)['__formsHarnessPatched'] = true;
      window.fetch = (async () =>
        new Response(JSON.stringify({ ok: true }), { status: 200, headers: { 'content-type': 'application/json' } })) as typeof fetch;
    }
    return <PublicFormPage initial={form} />;
  }

  return <PublicFormPage initial={form} />;
}
